import { createContext, useContext, useState } from "react";
import type { Team } from '@prisma/client';
type TeamPartial = Partial<Team>;
import { useAuth } from '@/contexts/auth.ctx.tsx';


interface TeamContextType {
    team: Team | null;
    teamSize: number;
    teamMembers: {id:string, name:string, fourWkCnt: number}[];
    refreshTeam: () => Promise<void>;
    loadTeamIntoCtx: (team: Team) => void;
    createTeam: (team: TeamPartial) => Promise<void>;
    updateTeam: (team: TeamPartial) => Promise<void>;
    deleteTeam: () => Promise<void>;
    joinTeam: (joinCode: string) => Promise<void>;
    leaveTeam: () => Promise<void>;
}

export interface TeamMember {id:string, name:string, fourWkCnt: number};

const TeamContext = createContext<TeamContextType | null>(null);

export const TeamProvider = ({ children }: { children: React.ReactNode }) => {
    const { injectHeader, accessorCan } = useAuth();
    const [team, setTeam] = useState<Team | null>(null);
    const [teamSize, setTeamSize] = useState<number>(0);
    const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);

    const refreshTeam = async () => {
        const req = new Request(`/api/auth/team`, {
            method: 'GET',
        });
        injectHeader(req);
        const res = await fetch(req);
        const data = await res.json() as {team:Team | null, teamSize:number, teamMembers?:TeamMember[]};
        setTeam(data.team);
        setTeamSize(data.teamSize);
        if(data.teamMembers){
            setTeamMembers(data.teamMembers);
        }
    }

    const loadTeamIntoCtx = (team: Team, teamSize?: number) => {
        setTeam(team);
        if(teamSize){
            setTeamSize(teamSize);
        }
    }

    const createTeam = async (team: TeamPartial) => {
        if(!accessorCan('create-team')){
            throw new Error('Accessor does not have permission to create teams');
        }
        const req = new Request(`/api/auth/team`, {
            method: 'POST',
            body: JSON.stringify(team)
        });
        injectHeader(req);
        const res = await fetch(req);
        const data = await res.json() as {team:Team, teamSize:number};
        setTeam(data.team);
        setTeamSize(data.teamSize);
        setTeamMembers([]);
    }

    const updateTeam = async (team: TeamPartial) => {
        if(!accessorCan('create-team')){
            throw new Error('Accessor does not have permission to update teams');
        }
        const req = new Request(`/api/auth/team`, {
            method: 'PUT',
            body: JSON.stringify(team)
        });
        injectHeader(req);
        const res = await fetch(req);
        const data = await res.json() as { team: Team};
        setTeam(data.team);
    }

    const deleteTeam = async () => {
        const req = new Request(`/api/auth/team`, {
            method: 'DELETE'
        });
        injectHeader(req);
        const res = await fetch(req);
        if(res.ok && res.status === 200){
            setTeam(null);
        } else {
            throw Error('Failed to delete team');
        }
    }

    const joinTeam = async (joinCode: string) => {
        if(!accessorCan('join-team')){
            throw new Error('Accessor does not have permission to join teams');
        }
        const req = new Request(`/api/auth/team?jc=${joinCode}`, {
            method: 'GET',
        });
        injectHeader(req);
        const res = await fetch(req);
        const data = await res.json() as {team:Team, teamSize:number};
        setTeam(data.team);
        setTeamSize(data.teamSize);
    }

    const leaveTeam = async () => {
        if(!team){
            throw new Error('No team to leave');
        }
        const req = new Request(`/api/auth/team?li=${team.id}`, {
            method: 'DELETE',
        });
        injectHeader(req);
        const res = await fetch(req);
        if(res.status === 200){
            setTeam(null);
        } else {
            throw new Error('Failed to leave team');
        }
    }

    return (
        <TeamContext.Provider value={{ team, teamSize, teamMembers, refreshTeam, loadTeamIntoCtx, createTeam, updateTeam, deleteTeam, joinTeam, leaveTeam }}>
            {children}
        </TeamContext.Provider>
    )
};

export const useTeam = () => {
    const context = useContext(TeamContext);
    if (!context) {
        throw new Error('useTeam must be used within a TeamProvider');
    }
    return context;
}